import React, { useEffect, useState, useCallback } from "react";
import api, { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { NumberInput } from "../components/NumberInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Target, Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const curMonth = () => new Date(Date.now() - 216e5).toISOString().slice(0, 7);
const curYear = () => new Date(Date.now() - 216e5).toISOString().slice(0, 4);

const Bar = ({ pct }) => (
  <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
    <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-green-600" : pct >= 60 ? "bg-primary" : "bg-amber-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

export default function MetasVenta() {
  const { can } = useAuth();
  const canManage = can("salesgoals:manage");
  const [view, setView] = useState("metas"); // metas | stats
  const [kind, setKind] = useState("month"); // month | year
  const [month, setMonth] = useState(curMonth());
  const [year, setYear] = useState(curYear());
  const period = kind === "month" ? month : year;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignVendor, setAssignVendor] = useState("");
  const [assignAmount, setAssignAmount] = useState(0);
  const [myAmount, setMyAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sales-goals?period=${period}`).then((r) => {
      setData(r.data);
      if (!r.data.can_assign && r.data.goals[0]) setMyAmount(r.data.goals[0].amount || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    api.get(`/sales-goals/stats?year=${year}`).then((r) => setStats(r.data)).catch(() => setStats(null)).finally(() => setStatsLoading(false));
  }, [year]);
  useEffect(() => { if (view === "stats") loadStats(); }, [view, loadStats]);

  const save = async (vendor_id, amount) => {
    setSaving(true);
    try {
      await api.post("/sales-goals", { vendor_id, period, amount: Number(amount) || 0 });
      toast.success("Meta guardada");
      setAssignVendor(""); setAssignAmount(0);
      load();
    } catch (e) { toast.error("No se pudo guardar la meta"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.delete(`/sales-goals/${id}`); load(); } catch { toast.error("No se pudo quitar"); } };

  const periodLabel = kind === "month" ? "del mes" : "del año";

  return (
    <div className="space-y-6" data-testid="metas-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Target className="h-6 w-6 text-primary" /><h1 className="font-heading text-2xl font-bold">Metas de Ventas</h1></div>
        <div className="flex items-center gap-1 rounded-sm border p-0.5">
          <Button size="sm" variant={view === "metas" ? "default" : "ghost"} className="rounded-sm h-8 text-xs" onClick={() => setView("metas")} data-testid="metas-tab-goals"><Target className="h-3.5 w-3.5 mr-1" /> Metas</Button>
          <Button size="sm" variant={view === "stats" ? "default" : "ghost"} className="rounded-sm h-8 text-xs" onClick={() => setView("stats")} data-testid="metas-tab-stats"><BarChart3 className="h-3.5 w-3.5 mr-1" /> Estadísticas</Button>
        </div>
      </div>

      {view === "metas" ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-sm border p-0.5">
              <Button size="sm" variant={kind === "month" ? "default" : "ghost"} className="rounded-sm h-8 text-xs" onClick={() => setKind("month")} data-testid="metas-kind-month">Por mes</Button>
              <Button size="sm" variant={kind === "year" ? "default" : "ghost"} className="rounded-sm h-8 text-xs" onClick={() => setKind("year")} data-testid="metas-kind-year">Por año</Button>
            </div>
            {kind === "month"
              ? <Input type="month" className="rounded-sm w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="metas-period-month" />
              : <Input type="number" min="2020" max="2100" className="rounded-sm w-28" value={year} onChange={(e) => setYear(e.target.value)} data-testid="metas-period-year" />}
          </div>

          {loading || !data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
            <>
              {canManage && data.can_assign && (
                <Card className="rounded-sm p-4 space-y-3" data-testid="metas-assign">
                  <h3 className="font-bold text-sm uppercase tracking-widest">Asignar meta {periodLabel} a un vendedor</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={assignVendor} onValueChange={setAssignVendor}>
                      <SelectTrigger className="rounded-sm sm:w-64" data-testid="metas-vendor"><SelectValue placeholder="Selecciona vendedor" /></SelectTrigger>
                      <SelectContent>{(data.vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <NumberInput className="rounded-sm sm:w-48" value={assignAmount} onValueChange={setAssignAmount} data-testid="metas-amount" placeholder="Meta (Q)" />
                    <Button className="rounded-sm font-bold" disabled={saving || !assignVendor} onClick={() => save(assignVendor, assignAmount)} data-testid="metas-assign-save">Guardar meta</Button>
                  </div>
                </Card>
              )}

              <div className="space-y-3">
                {data.goals.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay metas para este período.</p>}
                {data.goals.map((g) => (
                  <Card key={g.vendor_id} className="rounded-sm p-4" data-testid={`meta-row-${g.vendor_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-bold">{g.vendor_name || "—"}</span>
                      <span className="text-sm font-mono">
                        {money(g.currency, g.sold)} / {g.amount > 0 ? money(g.currency, g.amount) : "sin meta"}
                        {g.amount > 0 && <span className={`ml-1 font-bold ${g.pct >= 100 ? "text-green-600" : "text-primary"}`}>({g.pct}%)</span>}
                      </span>
                    </div>
                    {g.amount > 0 && <Bar pct={g.pct} />}
                    {canManage && data.can_assign && g.id && (
                      <button onClick={() => remove(g.id)} className="mt-2 text-xs text-red-600 flex items-center gap-1 hover:underline" data-testid={`meta-del-${g.vendor_id}`}>
                        <Trash2 className="h-3 w-3" /> Quitar meta
                      </button>
                    )}
                  </Card>
                ))}
              </div>

              {canManage && !data.can_assign && (
                <Card className="rounded-sm p-4 space-y-3" data-testid="metas-mine">
                  <h3 className="font-bold text-sm uppercase tracking-widest">Definir mi meta {periodLabel}</h3>
                  <div className="flex gap-2">
                    <NumberInput className="rounded-sm w-48" value={myAmount} onValueChange={setMyAmount} data-testid="metas-my-amount" />
                    <Button className="rounded-sm font-bold" disabled={saving} onClick={() => save("", myAmount)} data-testid="metas-my-save">Guardar</Button>
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      ) : (
        <div className="space-y-4" data-testid="metas-stats">
          <div className="flex items-center gap-2">
            <Label className="text-xs font-bold uppercase tracking-widest">Año</Label>
            <Input type="number" min="2020" max="2100" className="rounded-sm w-28" value={year} onChange={(e) => setYear(e.target.value)} data-testid="metas-stats-year" />
          </div>
          {statsLoading || !stats ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-sm border bg-card p-4"><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Meta del año</p><p className="text-2xl font-heading font-black leading-none mt-1.5">{money("GTQ", stats.year_goal)}</p></div>
                <div className="rounded-sm border bg-card p-4"><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Vendido en el año</p><p className="text-2xl font-heading font-black leading-none mt-1.5">{money("GTQ", stats.year_sold)}</p></div>
                <div className="rounded-sm border bg-card p-4"><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Cumplimiento</p><p className={`text-2xl font-heading font-black leading-none mt-1.5 ${stats.year_pct >= 100 ? "text-green-600" : "text-primary"}`}>{stats.year_pct}%</p></div>
              </div>
              {stats.year_goal > 0 && <Bar pct={stats.year_pct} />}
              <Card className="rounded-sm border shadow-none overflow-hidden">
                <div className="overflow-auto max-h-[60vh]">
                  <table className="w-full text-sm" data-testid="metas-stats-table">
                    <thead className="sticky top-0 bg-muted text-xs uppercase tracking-widest text-muted-foreground"><tr>
                      <th className="px-4 py-3 text-left font-bold">Mes</th>
                      <th className="px-4 py-3 text-right font-bold">Meta</th>
                      <th className="px-4 py-3 text-right font-bold">Vendido</th>
                      <th className="px-4 py-3 text-right font-bold">Cumplimiento</th>
                      <th className="px-4 py-3 font-bold w-40">Avance</th></tr></thead>
                    <tbody>
                      {stats.by_month.map((m) => (
                        <tr key={m.month} className="border-t border-border hover:bg-muted/40" data-testid={`metas-stats-${m.month}`}>
                          <td className="px-4 py-2.5 font-medium">{m.label}</td>
                          <td className="px-4 py-2.5 text-right font-mono">{m.goal > 0 ? money("GTQ", m.goal) : "—"}</td>
                          <td className="px-4 py-2.5 text-right font-mono">{money("GTQ", m.sold)}</td>
                          <td className={`px-4 py-2.5 text-right font-mono font-bold ${m.pct >= 100 ? "text-green-600" : m.goal > 0 ? "text-primary" : "text-muted-foreground"}`}>{m.goal > 0 ? `${m.pct}%` : "—"}</td>
                          <td className="px-4 py-2.5">{m.goal > 0 && <Bar pct={m.pct} />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
